<script lang="ts">
  import type { BeadColor } from '../catalog/types';
  import Modal from './Modal.svelte';

  /**
   * Guide des perles — modal listant TOUT le catalogue Hama Midi.
   * Référence visuelle accessible pendant création et pose guidée.
   *
   * Hauteur fixe (680px desktop) pour ne pas faire sauter le layout
   * quand l'user filtre via la search input.
   *
   * **Style** : Modal + .input + .hint pour le générique. Patterns
   * spécifiques (grid de cards perles + dot couleur) restent locaux.
   */

  type Props = {
    open: boolean;
    palette: ReadonlyArray<BeadColor>;
    onClose: () => void;
  };

  let { open, palette, onClose }: Props = $props();

  let filter = $state('');

  let filtered = $derived.by(() => {
    const f = filter.trim().toLowerCase();
    if (f === '') return palette;
    return palette.filter(
      (c) =>
        c.name.toLowerCase().includes(f) ||
        c.id.toLowerCase().includes(f),
    );
  });

  $effect(() => {
    if (open) filter = '';
  });

  // Bloque le scroll body en arrière-plan (Safari + contextes embed
  // ignorent parfois le lock natif du <dialog>.showModal()).
  $effect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  });
</script>

<Modal
  {open}
  title="Guide des perles"
  maxWidth={640}
  height={680}
  {onClose}
>
  <p class="hint">
    {palette.length} références Hama Midi, triées par teinte.
  </p>

  <input
    type="search"
    class="input"
    placeholder="Filtrer par nom ou code (ex : rouge, hama-205-05)…"
    bind:value={filter}
    autocomplete="off"
  />

  {#if filtered.length === 0}
    <p class="bg-empty">Aucune perle ne correspond à « {filter} ».</p>
  {:else}
    <ul class="bg-grid">
      {#each filtered as bead (bead.id)}
        <li class="bg-card">
          <span
            class="bg-card__dot"
            style={`--c: rgb(${bead.rgb.r},${bead.rgb.g},${bead.rgb.b})`}
          ></span>
          <div class="bg-card__meta">
            <strong class="bg-card__name">{bead.name}</strong>
            <small class="bg-card__code">{bead.id}</small>
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</Modal>

<style>
  /* Patterns spécifiques au guide perles : grid responsive de cards
   * "color dot + name + code". Non réutilisable ailleurs (le seul autre
   * grid de couleurs vit dans la palette éditeur qui a sa propre logique
   * de sélection). Reste local. */

  .bg-empty {
    margin: var(--space-2) 0 0;
    text-align: center;
    color: var(--color-text-muted);
    font-style: italic;
  }

  .bg-grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 6px;
    overflow-y: auto;
    min-height: 0;
    /* Laisse le scroll être sur le grid lui-même : le body Modal ne
     * scrolle pas, c'est le grid qui scrolle pour garder le filtre fixe. */
    flex: 1;
  }
  .bg-card {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) 10px;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
  }
  .bg-card__dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--c);
    border: 1px solid rgb(0 0 0 / 0.15);
    flex-shrink: 0;
  }
  .bg-card__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.2;
  }
  .bg-card__name {
    font-size: var(--text-small);
    color: var(--color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .bg-card__code {
    font-size: 10px;
    color: var(--color-text-muted);
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 640px) {
    .bg-grid {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    }
  }
</style>
